import React, { useMemo, useCallback } from 'react';
import { Flex, Text, List, ListItem, IconButton } from '@chakra-ui/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PaginationPageButton } from './PaginationPageButton';
import { PaginationEllipsisButton } from './PaginationEllipsisButton';

export interface IPagination {
  isFirstPage?: boolean;
  isLastPage?: boolean;
  currentPage: number;
  previousPage: number | null;
  nextPage: number | null;
  pageCount: number;
  totalCount: number;
  itemsPerPage?: number;
}

interface PaginationProps {
  pagination: IPagination;
  siblingCount?: number;
  boundaryCount?: number;
  onPageChange: (newPage: number) => void;
  onSizeChange?: (newSize: number) => void;
}

const ELLIPSIS_JUMP = 5;

export function Pagination({
  pagination,
  onPageChange,
  siblingCount = 1,
  boundaryCount = 1,
}: PaginationProps) {
  const { currentPage, pageCount, totalCount, itemsPerPage = 20 } = pagination;

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 1 && newPage <= pageCount) {
        onPageChange(newPage);
      }
    },
    [onPageChange, pageCount],
  );

  const memoizedRenderPageNumbers = useMemo(() => {
    const pageNumbers: React.ReactElement[] = [];
    for (let i = 1; i <= pageCount; i++) {
      if (
        i <= boundaryCount ||
        (i >= currentPage - siblingCount && i <= currentPage + siblingCount) ||
        i > pageCount - boundaryCount
      ) {
        pageNumbers.push(
          <ListItem key={i}>
            <PaginationPageButton
              pageNumber={i}
              currentPage={currentPage}
              onClick={handlePageChange}
            />
          </ListItem>,
        );
      } else if (i === boundaryCount + 1 && currentPage - siblingCount > boundaryCount + 1) {
        pageNumbers.push(
          <ListItem key={`ellipsis-prev-${i}`}>
            <PaginationEllipsisButton
              direction="prev"
              onClick={() => handlePageChange(Math.max(1, currentPage - ELLIPSIS_JUMP))}
            />
          </ListItem>,
        );
      } else if (
        i === pageCount - boundaryCount &&
        currentPage + siblingCount < pageCount - boundaryCount
      ) {
        pageNumbers.push(
          <ListItem key={`ellipsis-next-${i}`}>
            <PaginationEllipsisButton
              direction="next"
              onClick={() => handlePageChange(Math.min(pageCount, currentPage + ELLIPSIS_JUMP))}
            />
          </ListItem>,
        );
      }
    }
    return pageNumbers;
  }, [pageCount, boundaryCount, currentPage, siblingCount, handlePageChange]);

  const rangeStart = currentPage * itemsPerPage - itemsPerPage + 1 || 0;
  const rangeEnd = Math.min(currentPage * itemsPerPage, totalCount) || 0;

  return (
    <Flex justifyContent="space-between" alignItems="center" w="full">
      <Text fontSize="sm" color="fg.muted">
        Showing {rangeStart} to {rangeEnd} of {totalCount} results
      </Text>

      <Flex alignItems="center" gap={5}>
        <Text fontSize="sm" color="fg.muted">
          Page {currentPage} of {pageCount}
        </Text>

        <Flex gap={1}>
          <List display="flex" justifyContent="space-between" gap={1} alignItems="center">
            <ListItem>
              <IconButton
                aria-label="Previous page"
                icon={<ChevronLeft size={18} />}
                size="xs"
                variant="ghost"
                isDisabled={currentPage <= 1}
                onClick={() => handlePageChange(currentPage - 1)}
              />
            </ListItem>
            {memoizedRenderPageNumbers}
            <ListItem>
              <IconButton
                aria-label="Next page"
                icon={<ChevronRight size={18} />}
                size="xs"
                variant="ghost"
                isDisabled={currentPage >= pageCount}
                onClick={() => handlePageChange(currentPage + 1)}
              />
            </ListItem>
          </List>
        </Flex>
      </Flex>
    </Flex>
  );
}
